<template>
  <div class="m-1 flex-bigly">
    <div
      class="d-flex flex-row justify-content-between align-items-center pb-1"
    >
      <div>
        <icon-button
          id="createButton"
          label="Create"
          class="mr-1"
          type="primary"
          icon="cross"
          @click="openCreateModal"
        />
        <icon-button
          id="editButton"
          label="Edit"
          icon="pencil"
          type="primary-light"
          :disabled="!selectionExists"
          @click="openEditModal"
        />
      </div>
      <div class="d-flex flex-row">
        <input-box
          id="fuzzySearch"
          v-model="quickFilter"
          class="my-0"
          type="search"
          placeholder="Search all..."
          addon="start"
          data-cy="fuzzySearch"
        >
          <icon slot="addon-start" icon="search"></icon>
        </input-box>
        <icon-button
          id="refreshButton"
          title="Refresh"
          icon="refresh-ccw"
          class="ml-1 text-dark"
          type="secondary"
          outline
          data-cy="refreshButton"
          @click="refreshGrid"
        />
      </div>
    </div>

    <grid-tools
      :filter-action="toggleFilterPanel"
      :filter-active="showFilters"
      :column-action="toggleColumnSelectPanel"
      :column-active="showColumnSelect"
      :grid-options="gridOptions"
      @refreshGrid="refreshGrid"
      @clearFilters="$refs.filters.clearAll()"
      @showDefaultColumns="showDefaultColumns"
      @showAllColumns="showAllColumns"
    />

    <div class="flex-bigly-row grid-wrapper">
      <filter-panel
        v-if="gridApi"
        v-show="showFilters"
        ref="filters"
        class="panel panel-filters"
        :column-defs="gridOptions.columnDefs"
        :row-data="items"
        :grid-api="gridApi"
      />
      <ag-grid-vue
        ref="grid"
        class="grid grid-flex ag-theme-balham"
        :row-data="items"
        :grid-options="gridOptions"
        row-selection="single"
        @grid-ready="onGridReady"
        @row-double-clicked="openEditModal"
      />
      <columns-panel
        v-if="columnApi"
        v-show="showColumnSelect"
        ref="columns"
        class="panel panel-columns"
        :column-api="columnApi"
      />
    </div>

    <!-- Modal -->
    <modal
      id="vendorModal"
      ref="vendorModal"
      v-model="modalVisible"
      :title="modalTitle"
      role="dialog"
    >
      <template #default>
        <vendors-form v-if="modalVisible" id="form" />
      </template>
      <template slot="footer">
        <icon-button
          id="modalCancel"
          label="Cancel"
          type="accent"
          outline
          icon="x"
          @click="closeModal"
        />
        <icon-button
          id="modalClear"
          label="Clear"
          type="warning"
          outline
          icon="wind"
          @click="clearForm"
        />
        <icon-button
          id="modalSave"
          :disabled="disableSave"
          class="save"
          label="Save"
          type="primary"
          icon="save"
          @click="modalSave"
        />
      </template>
    </modal>
  </div>
</template>

<script>
// Components
import VendorsForm from "@/components/VendorsForm.vue"
import FilterPanel from "@/components/FilterPanel.vue"
import ColumnsPanel from "@/components/ColumnsPanel.vue"
import { AgGridVue } from "ag-grid-vue"
import GridTools from "@/components/GridTools.vue"
import IconButton from "@/components/IconButton.vue"
import InputBox from "@/components/InputBox.vue"
import Icon from "@/components/Icon.vue"
import Modal from "@/components/Modal.vue"
// Helpers, Functionality
import { mapActions, mapGetters } from "vuex"
import useGrid from "@/composables/useGrid"

export default {
  components: {
    VendorsForm,
    FilterPanel,
    ColumnsPanel,
    AgGridVue,
    GridTools,
    IconButton,
    InputBox,
    Icon,
    Modal,
  },
  beforeRouteLeave(to, from, next) {
    this.clearItem()
    next()
  },
  setup(_props) {
    const gridConfig = {
      columnDefs: [
        {
          headerName: "Name",
          field: "name",
          hide: false,
          menuTabs: ["generalMenuTab"],
        },
        {
          headerName: "Notes",
          field: "notes",
          hide: false,
          menuTabs: ["generalMenuTab"],
        },
        {
          headerName: "Website",
          field: "website",
          hide: false,
          menuTabs: ["generalMenuTab"],
        },
        {
          headerName: "Phone Number",
          field: "phone_number",
          hide: false,
          menuTabs: ["generalMenuTab"],
        },
        {
          headerName: "Email",
          field: "email",
          hide: false,
          menuTabs: ["generalMenuTab"],
        },
        {
          headerName: "Contact Name",
          field: "contact_name",
          hide: false,
          menuTabs: ["generalMenuTab"],
        },
        {
          headerName: "Active",
          field: "active",
          hide: false,
          menuTabs: ["generalMenuTab"],
          cellRenderer: "booleanCellRenderer",
        },
      ],
    }
    const gridDefaults = {
      sort: [{ colId: "name", sort: "asc" }],
      filter: {
        active: {
          filterType: "set",
          values: ["true"],
        },
      },
    }
    const {
      modal,
      gridApi,
      columnApi,
      gridOptions,
      quickFilter,
      showColumnSelect,
      showFilters,
      selectionExists,
      setGridDefaults,
      showDefaultColumns,
      showAllColumns,
      toggleFilterPanel,
      toggleColumnSelectPanel,
    } = useGrid(gridConfig, gridDefaults)
    return {
      modal,
      gridApi,
      columnApi,
      gridOptions,
      quickFilter,
      showColumnSelect,
      showFilters,
      selectionExists,
      setGridDefaults,
      showDefaultColumns,
      showAllColumns,
      toggleFilterPanel,
      toggleColumnSelectPanel,
    }
  },
  data() {
    return {
      disableSave: false,
      modalVisible: false,
    }
  },
  computed: {
    ...mapGetters("vendors", ["items", "activeItem", "errors"]),
    modalTitle: function () {
      if (this.modal.type === "create") {
        return "Create New Vendor"
      } else {
        return "Edit Vendor"
      }
    },
  },
  methods: {
    ...mapActions("vendors", [
      "put",
      "post",
      "setItem",
      "clearItem",
      "fetchAll",
    ]),
    openCreateModal() {
      this.clearItem()
      this.modalVisible = true
      this.modal.type = "create"
      this.modal.action = "post"
    },
    openEditModal() {
      this.setItem(this.gridApi.getSelectedRows()[0])
      this.modalVisible = true
      this.modal.type = "edit"
      this.modal.action = "put"
    },
    closeModal() {
      this.modal.type = ""
      this.modalVisible = false
    },
    async modalSave() {
      this.disableSave = true
      try {
        let response =
          this.modal.action === "post" ? await this.post() : await this.put()
        if (response !== null && !Object.keys(this.errors).length) {
          this.closeModal()
          this.refreshGrid()
        }
      } catch (error) {
        console.error("something went wrong on Save:")
        console.error(error)
      }
      this.disableSave = false
    },
    clearForm() {
      // Clear it
      this.clearItem()
    },
    async refreshGrid() {
      this.gridApi.showLoadingOverlay()
      try {
        await this.fetchAll()
        this.setGridDefaults()
        this.showDefaultColumns()
        this.$refs.filters.init()
      } catch (error) {
        console.error("something went wrong on Refresh: ")
        console.error(error)
      }
      this.gridApi.hideOverlay()
    },
    async onGridReady() {
      await this.$nextTick() // await existence of filters ref
      this.refreshGrid()
    },
  },
}
</script>
